<template>
  <page-section :title="$t('Integrations_Sportradar_Mappings')">
    <el-button
      slot="controls"
      type="primary"
      icon="el-icon-circle-plus"
      @click="addRow"
    >
      {{ $t('Integrations_Sportradar_Create_Mapping') }}
    </el-button>

    <rest-list-editable
      :payload="fetchEndpoint"
      :add-endpoint="saveEndpoint"
      :update-endpoint="getUpdateEndpoint"
      :filterable="false"
      :rules="rules"
      :pagination="false"
      ref="rest-list"
    >
      <rest-list-column-enum
        prop="event_type"
        :title="$t('Integrations_Sportradar_EventType')"
        :min-width="180"
        :editable="true"
        :sortable="false"
        :enum="getSportEvents"
      >
      </rest-list-column-enum>

      <rest-list-column
        prop="campaign_id"
        v-if="!model.params.liveActivities"
        :title="$t('Integrations_Sportradar_Campaign')"
        :min-width="180"
        :editable="true"
        :sortable="false"
      >
        <template slot-scope="{ row }">
          {{ row.campaign_title || row.campaign_id }}
        </template>
        <template slot="input-edit" slot-scope="slot">
          <el-select
            v-model="slot.model.campaign_id"
            value-key="campaign_id"
            filterable
            remote
            :remote-method="autocompleteSearch"
            :loading="searching"
            @focus="autocompleteSearch()"
          >
            <el-option
              v-for="item in options"
              :key="item.id"
              :label="item.title"
              :value="item.id"
            ></el-option>
          </el-select>
        </template>
      </rest-list-column>

      <rest-list-column-boolean
        v-if="model.params.liveActivities"
        prop="alert"
        :title="$t('Integrations_Sportradar_Alert_Label')"
        :editable="true"
      />

      <rest-list-column-actions :editable="true">
        <template slot-scope="scope">
          <rest-list-action-delete :endpoint="deleteEndpoint(scope.row)" />
        </template>
      </rest-list-column-actions>
    </rest-list-editable>
  </page-section>
</template>

<script>
import PageSection from '@/common/components/page/PageSection'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListEditable from '@/common/components/rest/RestListEditable'
import { projectEndpoint } from '@/core/helpers'
import { SportradarEventTypes } from '@/modules/integrations/types/sportradar/SportradarEventTypes'
import RestApi from '@/core/services/RestApi'
import memoize from 'memoizee'
import RestListColumnBoolean from '@/common/components/rest/RestListColumnBoolean.vue'

const fetchAttributes = memoize(
  queryString =>
    RestApi(
      projectEndpoint({
        method: 'get',
        url: `campaigns`,
        params: {
          title: queryString,
          status_type: 'live_past_campaigns',
          trigger_type: 'api'
        }
      })
    ).then(({ data }) => data),
  { promise: true, maxAge: 1000 }
)

export default {
  name: 'SportradarOverview',

  components: {
    RestListColumnBoolean,
    RestListEditable,
    RestListActionDelete,
    RestListColumnActions,
    PageSection,
    RestListColumn,
    RestListColumnEnum
  },

  props: {
    model: {
      required: false
    }
  },

  data() {
    return {
      options: [],
      searching: false,
      SportradarEventTypes
    }
  },

  computed: {
    rules() {
      return {
        event_type: {
          required: true,
          trigger: 'blur'
        },
        campaign_id: {
          required: true,
          trigger: 'blur'
        }
      }
    },

    fetchLiveActivitiesEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `integrations/sportradar/${this.$route.query.id}/live-activity-event`
      })
    },

    fetchEndpoint() {
      if (this.model.params.liveActivities) {
        return this.fetchLiveActivitiesEndpoint
      }

      return projectEndpoint({
        method: 'get',
        url: `integrations/sportradar/${this.$route.query.id}/mapping`
      })
    },

    saveLiveActivitiesEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `integrations/sportradar/${this.$route.query.id}/live-activity-event`
      })
    },

    saveEndpoint() {
      if (this.model.params.liveActivities) {
        return this.saveLiveActivitiesEndpoint
      }

      return projectEndpoint({
        method: 'post',
        url: `integrations/sportradar/${this.$route.query.id}/mapping`
      })
    },

    getSportEvents() {
      return {
        ...SportradarEventTypes['common'],
        ...SportradarEventTypes[this.model.params.sport]
      }
    },

    getUpdateEndpoint() {
      return this.model.params.liveActivities
        ? this.updateLiveActivitiesEndpoint
        : null
    }
  },

  methods: {
    addRow() {
      this.$refs['rest-list'].addRow({ campaign_id: null, event_type: null })
    },

    deleteLiveActivitiesEndpoint(row) {
      let eventType = encodeURIComponent(row.event_type)

      return projectEndpoint({
        method: 'delete',
        url: `integrations/sportradar/${this.$route.query.id}/live-activity-event/${eventType}`
      })
    },

    updateLiveActivitiesEndpoint(row) {
      let eventType = encodeURIComponent(row.event_type)

      return projectEndpoint({
        method: 'patch',
        url: `integrations/sportradar/${this.$route.query.id}/live-activity-event/${eventType}`
      })
    },

    deleteEndpoint(row) {
      if (this.model.params.liveActivities) {
        return this.deleteLiveActivitiesEndpoint(row)
      }

      let eventType = encodeURIComponent(row.event_type)
      let campaignId = encodeURIComponent(row.campaign_id)

      return projectEndpoint({
        method: 'delete',
        url: `integrations/sportradar/${this.$route.query.id}/mapping/${eventType}/${campaignId}`
      })
    },

    autocompleteSearch(queryString) {
      this.searching = true
      fetchAttributes(queryString).then(data => {
        this.options = data
        this.searching = false
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/integrations/types/sportradar/SportradarOverview.vue